import React, { memo, useEffect } from 'react';
import { Platform, View } from 'react-native';
import Color from 'color';
import Animated, { Easing, interpolate, useAnimatedStyle, useSharedValue, withRepeat, withTiming, } from 'react-native-reanimated';
import { rem } from 'design-system';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import { isWeb } from '../constants';
const Status = ({ active, activeText, inactiveText, inActiveColor, inactiveVariant = 'negative', style, }) => {
    const styles = useStyles(styleSet);
    const outerScale = useSharedValue(1);
    useEffect(() => {
        if (active && Platform.OS !== 'android' && !__DEV__) {
            const timeout = setTimeout(() => {
                outerScale.value = withRepeat(withTiming(0, { duration: 1000, easing: Easing.elastic(1) }), -1, true);
            }, 400);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
    }, [active, outerScale]);
    const outerStyle = useAnimatedStyle(() => {
        const size = interpolate(outerScale.value, [1, 0], [OUTER_WIDTH, INNER_WIDTH]);
        return {
            width: size,
            height: size,
        };
    }, [outerScale]);
    const colors = useColors();
    const inactive = inactiveVariant === 'informational'
        ? {
            outer: styles.informationalOuter,
            inner: styles.informationalInner,
            text: colors.text.statusInformational,
        }
        : {
            outer: styles.inactiveOuter,
            inner: styles.inactiveInner,
            text: colors.text.statusNegative,
        };
    return (<View style={[styles.outerContainer, style]}>
      <View style={styles.container}>
        <Animated.View style={[
            styles.outer,
            // eslint-disable-next-line no-nested-ternary
            active
                ? styles.activeOuter
                : inActiveColor
                    ? {
                        backgroundColor: Color(inActiveColor).fade(0.95).toString(),
                    }
                    : inactive.outer,
            outerStyle,
        ]}/>
        <View style={[
            styles.inner,
            // eslint-disable-next-line no-nested-ternary
            active
                ? styles.activeInner
                : inActiveColor
                    ? { backgroundColor: inActiveColor }
                    : inactive.inner,
        ]}/>
      </View>
      <Text Text-14 color={active ? colors.text.statusPositive : inActiveColor || inactive.text}>
        {active ? activeText : inactiveText}
      </Text>
    </View>);
};
const OUTER_WIDTH = rem(14);
const INNER_WIDTH = rem(6);
const INNER_PADDING = rem(4);
const styleSet = createStyleSheets((colors) => ({
    outerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && { display: 'flex' }),
    },
    container: {
        width: OUTER_WIDTH,
        height: OUTER_WIDTH,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(8),
    },
    outer: {
        width: INNER_WIDTH,
        height: INNER_WIDTH,
        borderRadius: OUTER_WIDTH,
    },
    inner: {
        width: INNER_WIDTH,
        height: INNER_WIDTH,
        borderRadius: INNER_WIDTH,
        position: 'absolute',
        top: INNER_PADDING,
        left: INNER_PADDING,
    },
    activeInner: {
        backgroundColor: colors.elements.statusPositive,
    },
    inactiveInner: {
        backgroundColor: colors.elements.statusNegative,
    },
    activeOuter: {
        backgroundColor: colors.elements.statusPositiveLight,
    },
    inactiveOuter: {
        backgroundColor: colors.elements.statusNegativeLight,
    },
    informationalInner: {
        backgroundColor: colors.elements.statusInformational,
    },
    informationalOuter: {
        backgroundColor: colors.elements.statusInformationalLight,
    },
}));
export default memo(Status);
